<template>
  <div>
    <vx-card class="mb-5" title="Learning & Resources">
      <h4 class="mb-5"><u>What is Physiotherapy?</u></h4>
      <p class="mb-5">
        The following video gives you a really good overview of what Physiotherapy is. You may have
        watched it when you chose this experience, but it’s worth reviewing again now.
      </p>
      <responsive-youtube-wrapper :video="videos[1].video" :image="videos[1].image" />

      <h4 class="mb-5"><u>The Economic Value of Physiotherapy</u></h4>
      <p class="mb-5">
        So, physiotherapists work across many areas including treating people’s pain and discomfort,
        assisting in the recovery from work or sport injuries and so much more. In doing so, they
        contribute a huge amount to society, but they also play a large role in contributing to the
        economy. It’s always hard to put a dollar value on this type of service, but the following
        video outlines a study that has put an economic value on the value of physiotherapy.
      </p>
      <responsive-youtube-wrapper :video="videos[2].video" :image="videos[2].image" />
    </vx-card>

    <vx-card>
      <h4 class="mb-5">Different Types of Physiotherapy</h4>
      <p class="mb-5">
        (Click the down arrows on the right to get descriptions and examples of each one)
      </p>
      <vs-collapse accordion>
        <vs-collapse-item v-for="(role, id) in roles" :key="id">
          <h5 slot="header">{{ role.title }}</h5>
          <responsive-youtube-wrapper v-if="role.has_video" :video="role.url" :image="role.image" />
          <p v-else>{{ role.text }}</p>
        </vs-collapse-item>
      </vs-collapse>
    </vx-card>

    <vx-card>
      <h4 class="mb-5">What does it mean to be a Physiotherapist or Occupational Therapist?</h4>
      <p class="mb-5">
        In this section, we will look at the differences between physiotherapy, chiropractic and
        osteopathy. While there are quite a few similarities, there are also many differences.
      </p>
      <responsive-youtube-wrapper :video="videos[3].video" :image="videos[3].image" class="mb-0" />
      <p class="mt-0 mb-5 text-center">
        (Please note this is a New Zealand video, so some of the educational information may differ
        slightly.)
      </p>

      <h4 class="mb-5">The Differences Between Physiotherapists, Chiropractors, and Osteopaths</h4>
      <responsive-youtube-wrapper :video="videos[4].video" :image="videos[4].image" />

      <vs-tabs alignment="fixed" :value="activeTab" class="overwrite-tabs">
        <vs-tab label="Physiotherapists">
          <h4 class="mb-5 mt-5">Physiotherapists</h4>
          <p class="mb-5">
            “Traditionally one may have seen a physiotherapist for some manual therapy and a
            movement based approach,” explains Stephen.
          </p>
          <p class="mb-5">
            While physios may use techniques such as manipulation, mobilisation or massage, usually
            the focus of physiotherapy treatment is on therapeutic exercise and self-management.
          </p>
          <p class="mb-5">
            You’ll be given exercises to do between appointments to assist recovery and improve your
            condition.
          </p>
          <p class="mb-5">
            Physiotherapists need a bachelor’s degree, but Stephen says these days a Master's degree
            or Doctor of Physiotherapy qualification is expected.
          </p>
          <p class="mb-5">
            Further study to qualify for fields such as sports and paediatric work requires an
            additional two years.
          </p>
          <p class="mb-5">
            Check if your physio is registered with the
            <a href="https://www.physiotherapyboard.gov.au/" target="_blank"
              >Physiotherapy Board of Australia</a
            >
          </p>
        </vs-tab>

        <vs-tab label="Chiropractors">
          <h4 class="mb-5 mt-5">Chiropractors</h4>
          <p class="mb-5">
            <a
              href="https://www.bodybraincentre.com.au/practitioners/dr-cassie-atkinson-quinton"
              target="_blank"
              >Dr Atkinson-Quinton</a
            >
            says chiropractors have the most training in joint manipulations or adjustments to the
            spine, arms and legs.
          </p>
          <p class="mb-5">
            “We all learn about joint mobilisations (gently wiggling joints), massage and stretches,
            home rehab exercises and technologies such as laser and ultrasound therapy,” she says.
          </p>
          <p class="mb-5">
            “Chiropractors and osteopaths tend to have a more holistic view by looking at physical,
            chemical and emotional influences on health rather than just the pain itself.
          </p>
          <p class="mb-5">
            “For example, migraines can have lots of contributing factors including neck, shoulder
            and cranial biomechanics, as well as food and stress triggers.”
          </p>
          <p class="mb-5">
            Refer to the
            <a href="https://www.chiropracticboard.gov.au/" target="_blank"
              >Chiropractic Board of Australia</a
            >
            for a list of registered chiropractors.
          </p>
        </vs-tab>

        <vs-tab label="Osteopaths">
          <h4 class="mb-5 mt-5">Osteopaths</h4>
          <p class="mb-5">
            “An osteopath would primarily treat with manual therapy,” says osteopath and
            physiotherapist
            <a href="https://www.injuryrehab.com.au/dr-stephen-king" target="_blank">Stephen King</a
            >.
          </p>
          <p class="mb-5">
            “For a musculoskeletal consultation, people can expect a similar approach to an
            appointment whether they see an osteopath or physiotherapist.”
          </p>
          <p class="mb-5">
            “Both professionals will perform a thorough clinical history to help understand the
            individual and their current complaint, and a thorough assessment to identify the cause
            of the problem.”
          </p>
          <p class="mb-5">Both will use a combination of manual therapy and exercise as well.</p>
          <p class="mb-5">So why opt for an osteo instead of a physio?</p>
          <p class="mb-5">
            “It comes down to personal bias, past experience of themselves, family and friends, and
            lack of success elsewhere,” says Stephen.
          </p>
          <p class="mb-5">
            Registered osteopaths can be found at the
            <a href="https://www.osteopathyboard.gov.au/" target="_blank"
              >Osteopathy Board of Australia</a
            >.
          </p>
        </vs-tab>
      </vs-tabs>

      <h4 class="mb-5">The Role of Physiotherapy in COVID-19 patients</h4>
      <responsive-youtube-wrapper :video="videos[5].video" :image="videos[5].image" />
    </vx-card>
  </div>
</template>

<script>
import store from '@/store/store';

export default {
  data() {
    return {
      videos: {
        1: {
          video: 'https://www.youtube.com/embed/ormYpdXMJRE',
          image: require('@/assets/images/experiences/physiotherapy/what_is_physio_splash.jpg'),
        },
        2: {
          video: 'https://www.youtube.com/embed/DCOzh4Au8B8',
          image: require('@/assets/images/experiences/physiotherapy/economic_value_splash.jpg'),
        },
        3: {
          video: 'https://www.youtube.com/embed/BiK0w39ZvlM',
          image: require('@/assets/images/experiences/physiotherapy/physio_ot_splash.jpg'),
        },
        4: {
          video: 'https://www.youtube.com/embed/OqdcfWESmWg',
          image: require('@/assets/images/experiences/physiotherapy/physio_chiro_osteo_splash.jpg'),
        },
        5: {
          video: 'https://www.youtube.com/embed/8xzo3LqZSvQ',
          image: require('@/assets/images/experiences/physiotherapy/covid19_splash.jpg'),
        },
      },
      activeTab: 0,
      roles: {
        1: {
          title: 'Musculoskeletal Physiotherapy',
          text: 'Otherwise known as orthopaedic physiotherapy, treatments that fall under this category focus on restoring function to the musculoskeletal system. This includes muscles, joints, tendons, ligaments, nerves and bones, that can be affected by various injuries, activities and movements.',
        },
        2: {
          title: 'Geriatric Physiotherapy',
          text: 'Geriatric physiotherapy focuses on the unique movement needs of older adults. There are many conditions that affect people as they grow older such as arthritis, cancer, osteoporosis, Alzheimer’s disease, joint replacements and balance disorders.',
        },
        3: {
          title: "Children's Physiotherapy",
          text: 'Children’s physiotherapy focuses on the physical needs of infants, toddlers, children and adolescents with a variety of developmental, neuromuscular, skeletal or other physical disorders.',
        },
        4: {
          title: 'Sports Physiotherapy',
          text: 'Sports physiotherapists are closely involved in the care and wellbeing of athletes. This area of practice includes management of athletic injuries.',
        },
        5: {
          title: 'Women’s Health Physiotherapy',
          text: 'Physiotherapy for women mainly addresses issues to the female reproductive system, childbirth, prenatal and postnatal care and issues with infertility.',
        },
        6: {
          title: 'Rehabilitation and Pain Management',
          text: 'For those suffering from chronic pain, due for surgery or had surgery, this type of physiotherapy focuses on getting individuals back on track to a pain-free lifestyle.',
        },
        7: {
          title: 'Cardiorespiratory Physiotherapy',
          text: 'Cardiorespiratory physiotherapy specialises in the prevention, rehabilitation and compensation of people suffering from diseases or injuries affecting the heart, chest and lungs. ',
        },
        8: {
          title: 'Neurological Physiotherapy',
          text: 'Neurological physiotherapy targets disorders affecting the brain and nervous system, including head injuries, stroke, spinal cord damage, aneurysm, multiple sclerosis, Parkinson’s disease and Cerebral Palsy for example. It also helps to treat neurological balance issues that can arise due to conditions such as vertigo.',
        },
        9: {
          title: 'Where do Physiotherapists Work?',
          text: 'Physiotherapists work in many healthcare sectors, including hospitals, community health centres, rehabilitation centres, private practice, and sporting clubs.',
        },
        10: {
          title: 'What Does it Mean to be a Physiotherapist? ',
          has_video: true,
          url: 'https://www.youtube.com/embed/SFx3S3EV_QQ',
          image: require('@/assets/images/experiences/physiotherapy/what_mean_physio_splash.jpg'),
        },
      },
    };
  },
  computed: {
    activeExperienceID() {
      return store.getters['authStudent/activeExperienceID'];
    },
    experience() {
      const loginType = localStorage.getItem('login_type');
      if (loginType == 'student') {
        const experiences = store.getters['authStudent/experiences'];
        return experiences.find((i) => i.id == this.activeExperienceID);
      } else {
        return store.getters['authTeacher/experience'];
      }
    },
  },
  beforeMount() {
    store.commit('authStudent/setActiveExperienceID', this.$route.params.id);
  },
};
</script>
